import React, { PropsWithChildren, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { sharedCopy } from '../../../data/static-content/shared/shared-content';
import {
  BUTTON_TYPES,
  CARD_WRAPPER_TYPES,
  CONTENTFUL_SLUGS,
  ICON_TYPES,
} from '../../../shared/constants';
import { IMdBody } from '../../../types/shared';

import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { ReactMarkdownExtended } from '../../../utils/generic';
import { languageHelper } from '../../../utils/language';
import { parseSectionID } from '../../../utils/string-utils';

import mq from '../../../styles/breakpoints';
import { H2, IntroText, ProductPageH2 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

interface Props extends PropsWithChildren {
  title: string;
  subtitle: string;
  description?: IMdBody;
  type: string;
  children: ReactNode;
  anchorName?: string;
  showOverviewLink?: boolean;
}

const CardWrapper = ({
  title,
  subtitle,
  description,
  type,
  children,
  anchorName,
  showOverviewLink,
}: Props) => {
  const id = parseSectionID(type, anchorName);
  const { lang } = languageHelper();

  return (
    <Container id={id}>
      {showOverviewLink && (
        <SectionHeader>
          {title && <AltTitle>{title}</AltTitle>}
          {subtitle && <StyledIntroText>{subtitle}</StyledIntroText>}
          <SecondaryButton // TODO: Best would be to let the link come from Contentful, e.g linked page model.
            to={`${CONTENTFUL_SLUGS.productOverview}/`}
            label={sharedCopy.cardWrappers.overviewLinkLabel[lang]}
            type={BUTTON_TYPES.internal}
            deqComponentTitle={title as string}
            icon={ICON_TYPES.chevron}
          />
        </SectionHeader>
      )}
      {(title || subtitle) && !showOverviewLink && (
        <TextContainer>
          {title && <Title>{title}</Title>}
          {subtitle && <StyledIntroText>{subtitle}</StyledIntroText>}
        </TextContainer>
      )}
      <Body type={type} amountOfItems={children?.length}>
        {children}
      </Body>
      {description && type === CARD_WRAPPER_TYPES.uspWrapper && (
        <Description>
          <ReactMarkdownExtended componentType={type.toLowerCase()}>
            {description?.childMarkdownRemark.rawMarkdownBody}
          </ReactMarkdownExtended>
        </Description>
      )}
    </Container>
  );
};

export default CardWrapper;

const MultipleChildren = css<{ amountOfItems: number }>`
  display: grid;
  gap: ${Spacings.M};
  grid-gap: ${Spacings.M};

  ${mq.from.breakpoint.M`
    grid-row-gap: ${Spacings.M};
    grid-column-gap: ${Spacings.M};

    ${({ amountOfItems }: { amountOfItems: number }) =>
      amountOfItems < 3
        ? `grid-template-columns: repeat(${amountOfItems}, minmax(0, auto));`
        : `grid-template-columns: repeat(3, minmax(0, 1fr));  width: 100%;`}
    ${({ amountOfItems }: { amountOfItems: number }) =>
      amountOfItems === 2 &&
      css`
        > a {
          &:nth-child(odd) {
            justify-self: flex-end;
          }
        }
      `}
  `}

  > * {
    margin-bottom: 0;
  }
`;

const TileContainer = css`
  flex-wrap: wrap;
  gap: ${Spacings.XS};

  ${mq.from.breakpoint.M`
    gap: ${Spacings.XS};
  `}

  ${mq.from.breakpoint.L`
    gap: ${Spacings.M};
    flex-direction: row;
  `}
`;

const StoryCardsContainer = css<{ amountOfItems: number }>`
  display: grid;
  grid-gap: ${Spacings.M};
  grid-template-columns: repeat(1, 1fr);
  width: 100%;

  ${mq.from.breakpoint.M`
      ${({ amountOfItems }: { amountOfItems: number }) =>
        amountOfItems < 2
          ? `grid-template-columns: repeat(${amountOfItems}, 1fr)`
          : `grid-template-columns: repeat(2, 1fr)`};
  `}
  ${mq.from.breakpoint.L`
      ${({ amountOfItems }: { amountOfItems: number }) =>
        amountOfItems < 3
          ? `grid-template-columns: repeat(${amountOfItems}, 1fr)`
          : `grid-template-columns: repeat(3, 1fr)`};
              grid-gap: 24px;

  `}  
   ${mq.from.breakpoint.XL`
   ${({ amountOfItems }: { amountOfItems: number }) =>
     amountOfItems < 4
       ? `grid-template-columns: repeat(${amountOfItems}, 1fr)`
       : `grid-template-columns: repeat(4, 1fr)`};
  `}
`;

const LinkCardsContainer = css<{ amountOfItems: number }>`
  display: grid;
  grid-gap: ${Spacings.S};
  grid-template-columns: repeat(1, 1fr);
  width: 100%;

  ${mq.from.breakpoint.M`
    grid-gap: ${Spacings.M};

    ${({ amountOfItems = 2 }: { amountOfItems: number }) =>
      `grid-template-columns: repeat(${amountOfItems > 1 ? 2 : 1}, 1fr)`}
  `}
`;

const StepsContainer = css<{ type: string; amountOfItems: number }>`
  gap: 0px ${Spacings.XL};
  width: 100%;

  ${({ type }) =>
    type === CARD_WRAPPER_TYPES.stepVerticalWrapper &&
    css`
      ${mq.from.breakpoint.M`
        width: 75%;
      `}

      ${mq.from.breakpoint.L`
        width: 50%;
        grid-gap: 0;
      `}
    `}

  ${({ type, amountOfItems }) =>
    type === CARD_WRAPPER_TYPES.stepHorizontalWrapper &&
    css`
      align-items: baseline;
      display: grid;
      grid-template-columns: 1fr;

      ${mq.from.breakpoint.M`
        width: 100%;
        grid-gap: ${Spacings.M};
        row-gap: 80px;
        grid-template-columns: repeat(4, 1fr);
      `}

      ${mq.from.breakpoint.XL`
        grid-gap: ${Spacings.XL};
        grid-template-columns: repeat(${
          amountOfItems < 4 ? amountOfItems : '4'
        }, 1fr);
      `}
    `}
`;

const QuoteWrapperContainer = css`
  gap: ${Spacings.S};

  ${mq.from.breakpoint.M`
    gap: 80px;
  `}

  ${mq.from.breakpoint.L`
    flex-direction: row;
  `}
`;

const Container = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: auto;
  justify-content: center;
  width: 100%;
`;

const TextContainer = styled.div`
  margin-bottom: ${Spacings.XL};
  width: 100%;

  ${mq.from.breakpoint.M`
    margin-bottom: 60px;
  `}
`;

const Title = styled(ProductPageH2)`
  margin: 0;
  text-align: center;
  width: 100%;
`;

const AltTitle = styled(H2)`
  margin: 0;
`;

const StyledIntroText = styled(IntroText)`
  display: block;
  margin-top: ${Spacings.XS};
  text-align: center;

  ${mq.from.breakpoint.M`
    margin-top: ${Spacings.S};
  `}
`;

const SectionHeader = styled.div`
  align-items: baseline;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${Spacings.M};
  width: 100%;

  ${mq.from.breakpoint.M`
    align-items: center;
    min-height: ${Spacings.XL};
  `}

  ${mq.from.breakpoint.L`
    min-height: 60px;
  `}

  ${mq.from.breakpoint.XL`
    margin-bottom: ${Spacings.L};
  `}
`;

const CardsContainer = css`
  width: 100%;
`;

const Body = styled.div<{ type: string; amountOfItems?: number }>`
  display: flex;
  flex-direction: column;
  grid-gap: ${Spacings.S};
  height: auto;
  justify-content: center;
  width: 100%;

  > * {
    &:last-of-type {
      gap-bottom: 0;
    }
  }

  ${mq.from.breakpoint.M`
    grid-gap: 80px;
  `}

  ${({ type }) => {
    switch (type) {
      case CARD_WRAPPER_TYPES.stepHorizontalWrapper:
      case CARD_WRAPPER_TYPES.stepVerticalWrapper:
        return StepsContainer;
      case CARD_WRAPPER_TYPES.storyCardsWrapper:
        return StoryCardsContainer;
      case CARD_WRAPPER_TYPES.linkCardsWrapper:
        return LinkCardsContainer;
      case CARD_WRAPPER_TYPES.previewCardWrapper:
      case CARD_WRAPPER_TYPES.uspWrapper:
        return MultipleChildren;
      case CARD_WRAPPER_TYPES.tileWrapper:
        return TileContainer;
      case CARD_WRAPPER_TYPES.quoteWrapper:
        return QuoteWrapperContainer;
      case CARD_WRAPPER_TYPES.anchorWrapper:
      case CARD_WRAPPER_TYPES.promovisualWrapper:
        return CardsContainer;
      default:
    }
  }}
`;

const Description = styled.div`
  color: ${({ theme }) => theme.colors.abnNeutral400};
  font-size: 14px;
  line-height: 20px;
  margin-top: ${Spacings.M};
  text-align: center;

  ${mq.from.breakpoint.M`
    margin-top: ${Spacings.L};
  `}

  ${mq.from.breakpoint.L`
    margin-top: ${Spacings.XL};
  `}

  & > *:first-child {
    margin-top: 0;
  }

  & > *:last-child {
    margin-bottom: 0;
  }
`;
